/* eslint-disable @next/next/no-img-element */
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Col, Row } from 'antd'
import cx from 'classnames'
import get from 'lodash/get'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import Head from 'next/head'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { CSSTransition } from 'react-transition-group'

import ContinueBooking from '../widgets/ContinueBooking'
import HotLeisureTips from './widgets/HotLeisureTips'
import Map from './widgets/Map'
import QuoteDescription from './widgets/QuoteDescription'
import Services from './widgets/Services'
import { interpolate, useTranslations } from 'common/language'
import ModalTrigger from 'common/modals/ModalTrigger'
import { getOgImage } from 'common/utils/getOgImage'
import { BlockSectionTitle, BlockWrapper, Button, Container, QuoteCard, Typography } from 'common/widgets'
import { ExperienceMasonry, ProductReviews, SinglePageAmenity, SinglePageHero, SinglePageHighlights, SinglePageNationality } from 'common/widgets/blocks'
import useSessionStorage from 'hooks/useSessionStorage'
import widgetClassNames from '../widgets/styles.module.scss'
import classNames from './styles.module.scss'


const cartIcon = ['fal', 'shopping-cart']

QuotePageView.propTypes = {
  openSidebar: PropTypes.bool,
  isPrintView: PropTypes.bool,
  withoutButton: PropTypes.bool,
  toggleSidebar: PropTypes.func,
  optionIndex: PropTypes.number,
  option: PropTypes.object.isRequired,
  quoteListData: PropTypes.object,
  quote: PropTypes.string.isRequired,
  optionId: PropTypes.string.isRequired,
}

export default function QuotePageView({
  toggleSidebar = noop,
  openSidebar = false,
  optionIndex = 1,
  option,
  quote,
  optionId,
  quoteListData = {},
  withoutButton = false,
  isPrintView = false,
}) {
  const { gettext, languageCode } = useTranslations()
  const [isScrolled, setScrolled] = useState(false)
  const { query } = useRouter()
  const [, setBookingData] = useSessionStorage('bookingData')
  const countryInfo = option.school?.static_content?.country?.info
  const schoolCoordinates = `${option?.school?.static_content?.location?.lat},${option?.school?.static_content?.location?.lng}`
  const mapSrc = `https://maps.googleapis.com/maps/api/staticmap?markers=${schoolCoordinates}&zoom=14&scale=2&size=640x320&language=${languageCode}&key=${process.env.GOOGLE_MAP_KEY}`
  const handleScroll = useCallback(_ => {
    if(!(typeof window === 'undefined')) {
      setScrolled(window.pageYOffset > 0)
    }
  }, [setScrolled, (typeof window === 'undefined')])
  useEffect(() => {
    if(!(typeof window === 'undefined')) {
      window.addEventListener('scroll', handleScroll)
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll, (typeof window === 'undefined')])
  useLayoutEffect(() => {
    const hash = (window?.location?.hash || '').replace('#', '')
    if(hash && !isPrintView) {
      // Fix for chrome not scrolling to hash on the page natively
      document.getElementById(hash)?.scrollIntoView()
    }
  }, [])
  const heroImage = useMemo(() => {
    const firstImage = get(option.school, 'static_content.media_images[0]', {})
    return {
      data: {
        name: firstImage.data?.[`name_${languageCode}`],
        alttext: firstImage.data?.[`alttext_${languageCode}`],
      },
      bynder: firstImage.bynder,
    }
  }, [option.school])
  const schoolHeroData = useMemo(() => ({
    title: get(option.school, 'name'),
    subtitle: get(option.school, 'destination.name'),
    image: heroImage,
  }), [option.school])
  const ogImage = getOgImage({
    value: {
      image: heroImage,
    },
  })
  const origin = useMemo(() => typeof window === 'undefined' ? process.env.ORIGIN_URL : window.location.origin, [])
  return (
    <Fragment>
      {!isPrintView && (
        <Head>
          <title>{schoolHeroData.title}</title>
          <meta name="description" content={schoolHeroData.subtitle} key="description" />
          <meta property="og:title" content={schoolHeroData.title} key="og-title" />
          <meta property="og:description" content={schoolHeroData.subtitle} key="og-description" />
          <meta property="og:image" content={ogImage} key="og-image" />
          <meta property="og:url" content={origin + '/' + languageCode + '/quote/list/' + quote + '/detail/' + optionId} key="og-url" />
          <meta property="og:type" content="website" key="og-type" />
          <meta name="robots" content="noindex" />
        </Head>
      )}
      <div className={classNames.quoteSinglePage}>
        <div className={classNames.printTitleWithHero}>
          <Container size="xl">
            <Typography variant="h2" className={classNames.optionName}>
              {interpolate(gettext('Option %s'), [optionIndex])}
            </Typography>
          </Container>
          <SinglePageHero data={schoolHeroData} />
        </div>
        <QuoteDescription school={option.school} />
        <SinglePageAmenity data={option.school?.static_content?.amenities} />
        <Container className={classNames.HighWrapper} size="xl">
          <Row className={classNames.highWrapperRow}>
            <Col
              xs={24}
              lg={13}
            >
              <SinglePageHighlights data={option.school?.static_content?.highlights} />
            </Col>
            <Col xs={0} lg={1} />
            <Col
              xs={24}
              lg={9}
            >
              <SinglePageNationality data={option.school?.static_content?.nationality_mix} />
            </Col>
          </Row>
        </Container>
        <Container size="xl">
          {isPrintView
            ? <img src={mapSrc} alt="Map view for printing" className={classNames.mapImg} />
            : <Map school={option.school} />}
        </Container>
        <Container size="xl" className={classNames.servicesTitleWrapper}>
          <BlockSectionTitle
            className={classNames.screenView}
            heading={gettext('Your Selection')}
          />
        </Container>
        <div className={classNames.CartWrapper}>
          <Container className={classNames.CartContainer} size="xl">
            <Typography
              variant="h2"
              className={classNames.printView}
            >
              {interpolate(gettext('Option %s'), [optionIndex])}
            </Typography>
            <Typography
              variant="h2"
              className={classNames.printView}
            >
              {gettext('Your Selection')}
            </Typography>
            <Row gutter={{ xs: 0, lg: 20 }} className={classNames.servicePrintRow}>
              <CSSTransition
                in={openSidebar}
                timeout={500}
                classNames="transition-cart"
              >
                <Col
                  xs={0}
                  lg={7}
                  md={7}
                  sm={7}
                  className={classNames.cart}
                >
                  <Row className={classNames.cartHeader}>
                    <Button onClick={toggleSidebar} type="icon" icon="arrow-left" classNameIcon={classNames.arrowButton} />
                    <Typography variant="h4">{gettext('Prices')}</Typography>
                    <span className={classNames.rightBlock} />
                  </Row>
                  <div className={classNames.quoteCartWrapper}>
                    <Typography
                      variant="h4"
                      transform="uppercase"
                      className={classNames.cartTitle}
                    >
                      {option.school.name}
                    </Typography>
                    <QuoteCard option={{ exchange_rate: option.exchange_rate, ...option.shopping_cart }} />
                  </div>
                  {countryInfo && <div className={classNames.countryHintWrapper}>
                    <Typography variant="small-gray-cart"
                      className={classNames.title}
                    ><span>{gettext('Hint')}</span></Typography>
                    <Typography variant="small-gray-cart">{countryInfo}</Typography>
                  </div>}
                  {!withoutButton && (
                    <div className={classNames.buttonRow}>
                      <ModalTrigger
                        title={gettext('continue booking')}
                        component={ContinueBooking}
                        setBookingData={setBookingData}
                        cart={option}
                        modalClassName={widgetClassNames.modalWrapper}
                        isNestedModal
                        state={{
                          firstName: quoteListData.firstName,
                          lastName: quoteListData.lastName,
                          quote: query.id,
                          option: query.option,
                        }}
                      >
                        <Button className={classNames.quoteCardButton}>
                          {gettext('continue booking')}
                        </Button>
                      </ModalTrigger>
                    </div>
                  )}
                </Col>
              </CSSTransition>
              <Col
                xs={24}
                lg={17}
              >
                <Services school={option.school_data} />
              </Col>
            </Row>
          </Container>
          <div className={classNames.triggerWrapper}>
            <Button
              onClick={toggleSidebar}
              icon={cartIcon}
            >
              <span
                className={cx(classNames.sideNav, isScrolled && classNames.scrolled)}
              >
                {gettext('Prices')}
              </span>
            </Button>
          </div>
        </div>
        <div className={classNames.screenView}>
          <Container size="xl">
            <BlockSectionTitle
              heading={gettext('Hot Leisure Tips')}
              {...!isPrintView ? { id: 'leisure-tips' } : {}}
            />
          </Container>
          <BlockWrapper>
            <Container size="xl">
              <HotLeisureTips school={option.school} />
            </Container>
          </BlockWrapper>
          <Container size="xl">
            <BlockSectionTitle
              heading={interpolate(gettext('%s Customer Reviews'), [option.school.name])}
              {...!isPrintView ? { id: 'reviews' } : {}}
            />
          </Container>
          <ProductReviews
            data={{
              kind: 'school',
              parent_id: option.school?.id,
              rating: option.school.static_content.rating.overall,
              reviews_count: option.school.static_content.rating.reviews_count,
              scores: omit(option.school.static_content.rating, ['reviews_count', 'overall']),
            }}
          />
          <Container size="xl">
            <BlockSectionTitle
              className={classNames.snapshotsTitle}
              heading={interpolate(gettext('%s Snapshots'), [option.school.name])}
            />
          </Container>
          <ExperienceMasonry
            data={{ kind: 'school', parent_id: option.school.id }}
            withoutCTA
          />
        </div>
      </div>
    </Fragment>
  )
}
